import React from 'react';
import { useParams } from 'react-router-dom';

const projects = [
  {
    id: 'ai-powered-codebase-qna-system-codelens',
    title: 'AI-Powered Codebase Q&A System (CodeLens)',
    subtitle: 'Python, FastAPI, RAG',
    description:
      'A developer-focused system that ingests any repository and answers natural-language questions with grounded references to real source files.',
    image: '/images/codelens-thumbnail.svg',
    technologies: ['Python', 'FastAPI', 'RAG', 'ChromaDB', 'HuggingFace Embeddings', 'Cross-Encoder Reranking', 'Groq/LLaMA3'],
    liveDemo: 'https://code-lens-san.vercel.app/',
    github: 'https://github.com/santhoshkumars2004/CodeLens',
    date: 'May 2026 - Present',
    details:
      'Built a 3-stage query pipeline with vector retrieval, cross-encoder reranking (ms-marco-MiniLM-L-12-v2), and LLM generation to improve answer quality for unfamiliar codebases. Implemented AST-based chunking and embedding with jina-embeddings-v2-base-code, then fixed retrieval pollution by skipping non-source ML artifacts before indexing. This increased retrieval confidence and reduced unnecessary token usage.',
  },
  {
    id: 'ai-powered-legal-query-system',
    title: 'AI-Powered Legal Query System',
    subtitle: 'Python, Django, AWS',
    description:
      'A secure legal-assist platform for police workflows that predicts relevant IPC/CrPC sections from complaint context and reduces manual legal lookup.',
    image: '/images/project1.jpg',
    technologies: ['Python', 'Django', 'AWS', 'Tesseract OCR', 'spaCy NLP', 'HTML', 'CSS'],
    github: 'https://github.com/Guruoo7/teja_rajasthan_hackathon',
    date: 'Jan 2024 - Feb 2024',
    details:
      'Built an end-to-end web app with authentication, restricted access, and a guided legal-drafting flow. Added OCR for uploaded Hindi complaint letters and integrated NLP-based section suggestions to improve drafting speed and accuracy. Designed the product for practical officer workflows and measurable reduction in manual section mapping effort.',
  },
];

const ProjectDetail = () => {
  const { projectId } = useParams();
  const project = projects.find((item) => item.id === projectId);

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center dark:bg-[#0B1020] dark:text-white">
        Project not found.
      </div>
    );
  }

  return (
    <div className="min-h-screen text-gray-800 dark:text-white py-20 pt-32">
      <div className="container mx-auto px-4">
        <h1 className="text-4xl md:text-5xl font-bold text-center mb-8 dark:text-white">{project.title}</h1>
        <p className="text-xl text-gray-600 text-center mb-4 dark:text-gray-300">{project.subtitle}</p>
        {project.date && <p className="text-lg text-gray-500 text-center mb-12 dark:text-gray-400">{project.date}</p>}

        <div className="flex flex-col md:flex-row gap-12">
          <div className="md:w-1/2">
            <img src={project.image} alt={project.title} className="w-full h-auto rounded-lg shadow-lg" />
          </div>

          <div className="md:w-1/2 text-gray-700 dark:text-gray-300">
            <h3 className="text-2xl font-semibold mb-4 dark:text-white">Description</h3>
            <p className="mb-6">{project.description}</p>

            <h3 className="text-2xl font-semibold mb-4 dark:text-white">More Details</h3>
            <div className="mb-6 whitespace-pre-line">{project.details}</div>

            <h3 className="text-2xl font-semibold mb-4 dark:text-white">Technologies</h3>
            <div className="flex flex-wrap gap-2 mb-6">
              {project.technologies.map((tech) => (
                <span
                  key={tech}
                  className="px-3 py-1 bg-white/80 backdrop-blur-sm rounded-full text-sm font-semibold text-gray-700 dark:bg-[#121A2B] dark:text-gray-200 border border-gray-200/50 dark:border-[#22314f]"
                >
                  {tech}
                </span>
              ))}
            </div>

            <div className="flex gap-4">
              {project.liveDemo && (
                <a
                  href={project.liveDemo}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-500 transition-colors"
                >
                  Live Demo
                </a>
              )}
              {project.github && (
                <a
                  href={project.github}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-colors dark:bg-blue-600 dark:hover:bg-blue-500"
                >
                  GitHub Repo
                </a>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectDetail;
